import { type ComponentProps, forwardRef, type ReactNode } from "react";
import { ChevronIcon, MoreHorizontalIcon } from "vega-icons";

import {
  type ButtonProps,
  buttonVariants,
} from "@vega-core/components/shadcn/button";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

const Pagination = ({ ...props }: ComponentProps<"nav">) => (
  <nav aria-label="pagination" role="navigation" {...props} />
);
Pagination.displayName = "Pagination";

const PaginationContent = forwardRef<HTMLUListElement, ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul
      className={cnTwMerge("flex flex-row items-center gap-1", className)}
      ref={ref}
      {...props}
    />
  ),
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = forwardRef<HTMLLIElement, ComponentProps<"li">>(
  ({ className, ...props }, ref) => (
    <li className={cnTwMerge("", className)} ref={ref} {...props} />
  ),
);
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = ComponentProps<"a"> &
  Pick<ButtonProps, "size"> & {
    isActive?: boolean;
    isLoading?: boolean;
  };

const PaginationLink = ({
  className,
  disabled = false,
  isActive,
  size = "icon",
  isLoading,
  ...props
}: PaginationLinkProps & { disabled?: boolean }) => (
  <a
    aria-current={isTruthy(isActive) ? "page" : undefined}
    aria-disabled={disabled}
    className={cnTwMerge(
      buttonVariants({
        variant: isTruthy(isActive) ? "activePagination" : "unActivePagination",
        size,
      }),
      className,
      { "skeleton-box": isLoading },
    )}
    onClick={(e) => {
      if (disabled || isTruthy(isLoading)) return;
      e.preventDefault();
      props.onClick?.(e);
    }}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  disabled = false,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cnTwMerge(
      "gap-1 p-0 text-text/primary border-1 shadow-card",
      className,
      disabled && "pointer-events-none opacity-50 ",
    )}
    disabled={disabled}
    size="default"
    {...props}
  >
    <ChevronIcon className="h-6 w-6 rotate-90" />
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  disabled = false,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cnTwMerge(
      "gap-1 p-0 text-text/primary border-1 shadow-card",
      className,
      disabled && "pointer-events-none opacity-50",
    )}
    disabled={disabled}
    size="default"
    {...props}
  >
    <ChevronIcon className="h-6 w-6 -rotate-90" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cnTwMerge("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontalIcon className="h-4 w-4" />
    <span className="sr-only">{m.common_morePages()}</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

const PaginationItemsInfo = ({
  className,
  isLoading,
  ...props
}: ComponentProps<"div"> & { isLoading?: boolean }) => (
  <div
    className={cnTwMerge(
      "body-base-medium text-text/secondary flex flex-col sm:flex-row items-center gap-2 sm:gap-6",
      className,
      {
        "skeleton-box": isLoading,
      },
    )}
    {...props}
  />
);
PaginationItemsInfo.displayName = "PaginationItemsInfo";

const PaginationWrapper = ({ children }: { children: ReactNode }) => (
  <div className="w-full flex flex-col sm:flex-row flex-wrap gap-2 justify-between items-center">
    {children}
  </div>
);
PaginationWrapper.displayName = "PaginationWrapper";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationItemsInfo,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationWrapper,
};
